import {
  Box,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  outlinedPickerStyles,
  pickerArrowStyles,
} from "Components/Shared/FormStyles";
import { ExpandArrowIcon } from "Components/Shared/Icons";
import { CheckboxInput } from "Components/Shared/Inputs/CheckboxInput";
import type { ReactNode, Ref } from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";

export const createLabelId = (fieldName: string) =>
  `dropdown-label-${fieldName}`;

type Option<TValue extends string | number> = {
  Value: TValue;
  Name: string;
  Icon?: ReactNode;
  disabled?: boolean;
};

type BaseProps<TValue extends string | number> = {
  name: string;
  options: Array<Option<TValue>>;
  defaultValue?: TValue | Array<TValue>;
  onBlur?: () => void;
  disabled?: boolean;
  label?: string;
  accessibilityLabel?: string;
  placeholder?: string;
  allSelectedText?: string;
  hasError?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  menuProps?: {
    PaperProps?: {
      sx?: SxProps<Theme>;
    };
  };
  isBorderless?: boolean;
};

type SingleProps<TValue extends string | number> = BaseProps<TValue> & {
  multiple?: false;
  value: TValue | undefined;
  onChange?: (value: TValue) => void;
};

type MultipleProps<TValue extends string | number> = BaseProps<TValue> & {
  multiple: true;
  value: Array<TValue> | undefined;
  onChange?: (value: Array<TValue>) => void;
};

type Props<TValue extends string | number> =
  | SingleProps<TValue>
  | MultipleProps<TValue>;

const StyledSelect = styled(Select)<{ $isBorderless?: boolean }>`
  &:before,
  &:after {
    content: none;
  }

  ${outlinedPickerStyles}

  .MuiSelect-select {
    min-height: auto;
    border-radius: 0;
  }

  .MuiOutlinedInput-notchedOutline {
    border: ${(props) => (props.$isBorderless ? "none" : undefined)};
  }

  &.Mui-disabled {
    .MuiSelect-select {
      color: ${(props) => props.theme.palette.text.primary}80;
    }

    .MuiOutlinedInput-notchedOutline {
      border: ${(props) => (props.$isBorderless ? "none" : undefined)};
    }
  }
`;

const StyledLabel = styled.div`
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const StyledIcon = styled.div`
  position: absolute;
  right: 16px;
  pointer-events: none;

  ${pickerArrowStyles}
`;

const DropdownIcon = () => (
  <StyledIcon>
    <ExpandArrowIcon />
  </StyledIcon>
);

// Icon next to StyledSelectedValue, which stays the ellipsis holder;
// min-width lets the text shrink inside the flex row.
const StyledSelectedValueWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(3)};
  min-width: 0;
`;

const StyledPlaceholder = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const StyledMenuItem = styled(MenuItem)`
  .MuiTouchRipple-root {
    color: ${(props) => props.theme.palette.primary.main};
  }

  .MuiCheckbox-root {
    padding: 0;
  }
`;

const StyledNoOptionsItem = styled(MenuItem)`
  color: ${(props) => props.theme.palette.text.secondary};
  font-style: italic;
  cursor: default;
  pointer-events: none;
  
  &:hover {
    background-color: transparent;
  }
`;

const StyledSelectedValue = styled.div<{ upperCase?: boolean }>`
  overflow: hidden;
  margin-right: 22px;
  text-overflow: ellipsis;
  text-transform: ${(props) => (props.upperCase ? "uppercase" : "none")};
  min-height: 16px;
  font-size: 16px;
  font-weight: 500;
`;

const renderSelectedValue = <TValue extends string | number>(
  value: TValue | Array<TValue>,
  { placeholder, multiple, options, allSelectedText }: Props<TValue>,
): ReactNode => {
  if (!value || (value as Array<string | number>).length === 0) {
    return placeholder ? (
      <StyledPlaceholder>{placeholder}</StyledPlaceholder>
    ) : (
      <StyledSelectedValue />
    );
  }

  // "All selected" needs a text to show; without one (a one-option list, say) the names are the better summary.
  if (
    multiple &&
    allSelectedText &&
    (value as Array<string | number>).length === options.length
  ) {
    return (
      <StyledSelectedValue upperCase>{allSelectedText}</StyledSelectedValue>
    );
  }

  if (multiple) {
    const text = options
      .filter((o) => (value as Array<TValue>).some((v) => v === o.Value))
      .map((x) => x.Name)
      .join(", ");

    return <StyledSelectedValue>{text}</StyledSelectedValue>;
  }

  const selected = options.find((x) => x.Value.toString() === value.toString());

  if (selected?.Icon) {
    return (
      <StyledSelectedValueWithIcon>
        {selected.Icon}
        <StyledSelectedValue>{selected.Name}</StyledSelectedValue>
      </StyledSelectedValueWithIcon>
    );
  }

  return <StyledSelectedValue>{selected?.Name}</StyledSelectedValue>;
};

export const Dropdown = <TValue extends string | number>(
  props: Props<TValue>,
) => {
  const { t } = useResource();

  const {
    options,
    value,
    disabled,
    name,
    onChange,
    multiple,
    label,
    onBlur,
    hasError,
    inputRef,
    defaultValue,
    accessibilityLabel,
    menuProps,
    isBorderless,
  } = props;

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    (onChange as ((value: TValue | Array<TValue>) => void) | undefined)?.(
      event.target.value as TValue | Array<TValue>,
    );
  };

  const hasOptions = options.length > 0;

  return (
    <div>
      {label && <StyledLabel>{label}</StyledLabel>}
      <StyledSelect
        value={value ?? (multiple ? [] : "")}
        labelId={createLabelId(name)}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={onBlur}
        inputProps={{
          "aria-label": accessibilityLabel || label || "No label provided", //TODO Nice to have, ideally we'd want to enforce to always require either a label or an accessibilityLabel
        }}
        IconComponent={DropdownIcon}
        disabled={disabled}
        multiple={multiple}
        error={hasError}
        fullWidth
        displayEmpty
        MenuProps={{
          variant: "menu",
          ...menuProps,
        }}
        renderValue={(value) =>
          renderSelectedValue(value as TValue | Array<TValue>, props)
        }
        variant="outlined"
        inputRef={inputRef}
        $isBorderless={isBorderless}
      >
        {hasOptions ? (
          options.map(({ Value, Name, Icon, disabled }) => (
            <StyledMenuItem
              key={`${name}-${Value}`}
              value={Value}
              disabled={disabled}
            >
              {multiple ? (
                <CheckboxInput
                  checked={(value as Array<TValue>).some((x) => x === Value)}
                  label={Name}
                />
              ) : (
                <Box
                  sx={{
                    fontSize: "1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {Icon}
                  {Name}
                </Box>
              )}
            </StyledMenuItem>
          ))
        ) : (
          <StyledNoOptionsItem disabled>
            {t(Resources.Components.Shared.Dropdown.NoOptions)}
          </StyledNoOptionsItem>
        )}
      </StyledSelect>
    </div>
  );
};
